// Device-side API client for the auth surface. Lives under
// apps/pos-web/src/data/ because that's the one place in the device
// bundle allowed to make API calls (CLAUDE.md § Code style). No business
// logic lives here — the server is authoritative.
//
// We intentionally do NOT persist the PIN or any session token in
// IndexedDB from here. The real register (P1-06) handles secure
// in-memory token storage; this helper is for the dev harness and
// returns the token to the caller for one-shot use.

const DEFAULT_API_URL = "http://localhost:3001";

function apiBase(): string {
  // Vite exposes VITE_* vars via import.meta.env.
  return (import.meta.env.VITE_API_URL as string | undefined) ?? DEFAULT_API_URL;
}

export type SignInInput = {
  deviceId: string;
  deviceCredential: string;
  staffId: string;
  pin: string;
};

export type SignInSuccess = {
  ok: true;
  token: string;
  sessionId: string;
  expiresIn: number;
  deviceCredentialRotated: boolean;
};

export type SignInError = {
  ok: false;
  status: number;
  error: string;
};

export async function signIn(input: SignInInput): Promise<SignInSuccess | SignInError> {
  let res: Response;
  try {
    res = await fetch(`${apiBase()}/auth/pin`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        device_id: input.deviceId,
        device_credential: input.deviceCredential,
        staff_id: input.staffId,
        pin: input.pin,
      }),
    });
  } catch (_e) {
    // Network-level failure: CORS block, DNS, offline, aborted. Browsers
    // report these as a thrown TypeError with no response. We never let
    // the UI hang on "Signing in…" — surface a soft error instead.
    return { ok: false, status: 0, error: "network_error" };
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: "unknown" }))) as { error?: string };
    return { ok: false, status: res.status, error: body.error ?? "unknown" };
  }
  const body = (await res.json()) as {
    token: string;
    session_id: string;
    expires_in: number;
    device_credential_rotated: boolean;
  };
  return {
    ok: true,
    token: body.token,
    sessionId: body.session_id,
    expiresIn: body.expires_in,
    deviceCredentialRotated: body.device_credential_rotated,
  };
}
